function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import concat from 'lodash/concat';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import stripDashesFromGuid from '@signal/utils/stripDashesFromGuid';
import { castFetchContext } from '../contextFetch/Fetcher';
import { useUserAccounts, useUserDetails } from './hooks';
import { getRecentlyViewedAccountsFromStorage, setRecentlyViewedAccountsInStorage } from './storage';
import { isUserDetails } from './types';
import { ActiveAccountContext, UserAccountsContext, UserDetailsContext } from './Context';
import { getPendoObject, setPendoIdentity } from '@signal/utils/pendo';
var UserAccountsFetcher = castFetchContext();
var UserDetailsFetcher = castFetchContext();
/**
 * @interface IActiveAccountSyncProps
 * this interface defines the properties accepted by the ActiveAccountSync component
 */

/**
 * @component DefaultActiveAccountSetter
 * The <ActiveAccountSetter> React component, which ensures the active account is the one whose accountId is in props
 */

function ActiveAccountSync(_ref) {
  var accountId = _ref.accountId,
      children = _ref.children;

  var _useContext = useContext(UserAccountsContext),
      response = _useContext.response;

  var context = useContext(ActiveAccountContext);
  var activeAccount = context.activeAccount,
      changeActiveAccount = context.changeActiveAccount;
  var activeAccountId = activeAccount && stripDashesFromGuid(activeAccount.accountId);
  useEffect(function () {
    if (accountId && changeActiveAccount && (!activeAccountId || stripDashesFromGuid(activeAccountId) !== stripDashesFromGuid(accountId)) && isArray(response)) {
      var account = response.find(function (_ref2) {
        var id = _ref2.accountId;
        return stripDashesFromGuid(id) === stripDashesFromGuid(accountId);
      });

      if (account) {
        changeActiveAccount(account, true
        /*fromSync*/
        );
      }
    }
  }, [accountId, activeAccountId, changeActiveAccount, response]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
}
/**
 * @interface PendoIndentityProps
 * This interface defines the props for the PendoIdentity Component
 */

/**
 * @component PendoIdentity
 * This component consumes the UserDetailsContext to set the Pendo identity
 */


function PendoIndentity(_ref3) {
  var children = _ref3.children;

  var _useContext2 = useContext(UserDetailsContext),
      userDetails = _useContext2.response,
      isFetching = _useContext2.isFetching;

  var pendo = getPendoObject();

  if (!pendo.visitorId && isUserDetails(userDetails) && !isFetching) {
    setPendoIdentity(userDetails);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
}
/**
 * @interface IUserLoaderParams
 * this interface defines the properties accepted by the UserLoader component
 */

/**
 * @component UserLoader
 * The <UserRoot> React component, which fetches user data using the context fetcher
 */


export function UserLoader(_ref4) {
  var accountId = _ref4.accountId,
      children = _ref4.children,
      onAccountChange = _ref4.onAccountChange;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      activeAccount = _useState2[0],
      setActiveAccount = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      recentAccounts = _useState4[0],
      setRecentAccounts = _useState4[1];

  var activeAccountContext = useMemo(function () {
    return {
      activeAccount: activeAccount,
      recentAccounts: recentAccounts,
      changeActiveAccount: function changeActiveAccount(account) {
        var fromSync = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var newRecentAccounts = uniq(concat([account.accountId], recentAccounts));
        setRecentlyViewedAccountsInStorage(newRecentAccounts);
        setRecentAccounts(newRecentAccounts);
        setActiveAccount(account);

        if (!fromSync) {
          onAccountChange(account);
        }
      }
    };
  }, [activeAccount, onAccountChange, recentAccounts]); // always set this, not in an effect, in case it is set by other existing code in Signal on the redux-saga
  // thread which can't see these React contexts

  var recentAccountsStorage = getRecentlyViewedAccountsFromStorage();

  if (!isEqual(recentAccounts, recentAccountsStorage)) {
    setRecentAccounts(recentAccountsStorage);
  }

  return /*#__PURE__*/React.createElement(ActiveAccountContext.Provider, {
    value: activeAccountContext
  }, /*#__PURE__*/React.createElement(UserAccountsFetcher, {
    fetchHook: useUserAccounts,
    FetchContext: UserAccountsContext
  }, /*#__PURE__*/React.createElement(UserDetailsFetcher, {
    fetchHook: useUserDetails,
    FetchContext: UserDetailsContext
  }, /*#__PURE__*/React.createElement(PendoIndentity, null, /*#__PURE__*/React.createElement(ActiveAccountSync, {
    accountId: accountId
  }, children)))));
}
export default /*#__PURE__*/memo(UserLoader);